import { Box, Button, ButtonText, Heading, HStack, Image, Pressable, ScrollView, Text, VStack } from '@gluestack-ui/themed';
import { ArrowLeftIcon } from 'lucide-react-native';
import { useContext, useMemo, useState, type FC } from 'react';
import { StyleSheet } from 'react-native';

import BackgroundSVG from '../assets/background.svg';
import { HypothesisComparison } from '../components/analysis/verrou/HypothesisComparison';
import { HypothesisSection } from '../components/analysis/HypothesisSection';
import { PrimaryButton } from '../components/buttons/PrimaryButton';
import { SecundaryButton } from '../components/buttons/SecundaryButton';
import { TranslationContext } from '../contexts/translations/Translation';
import { TEST_BANK, type OrganismKind, type TestBankSample } from '../data/testBank';
import type { ITestBankPageProps } from '../routes/Routes';
import { COLORS } from '../theme/colors';

// Practice game: pick a curated Motic image, guess, get instant match/try-again vs
// the PRE-BAKED answer. Gram stain -> guess Gram + shape; methylene blue -> guess
// the organism kind (bacteria/yeast/mould). Answers live in data/testBank.ts.

type Guess = { gram?: 'pos' | 'neg' | 'idk'; shape?: 'cocci' | 'bacillus' | 'idk'; kind?: OrganismKind };
const KINDS: OrganismKind[] = ['bacteria', 'yeast', 'mould'];
const concrete = (v?: string) => v === 'pos' || v === 'neg' || v === 'cocci' || v === 'bacillus';

const shuffle = <T,>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const TestBankPage: FC<ITestBankPageProps> = () => {
  const { i18n } = useContext(TranslationContext);
  const b = 'page.test-bank';

  // Reshuffle every time we return to the gallery so the top isn't always the same.
  const [shuffleKey, setShuffleKey] = useState(0);
  const samples = useMemo(() => shuffle(TEST_BANK), [shuffleKey]);

  const [selected, setSelected] = useState<TestBankSample | null>(null);
  const [guess, setGuess] = useState<Guess>({});
  const [checked, setChecked] = useState(false);
  const [solved, setSolved] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);

  const reset = () => { setGuess({}); setChecked(false); setSolved(false); setGaveUp(false); };
  const open = (s: TestBankSample) => { setSelected(s); reset(); };
  const backToGallery = () => { setSelected(null); reset(); setShuffleKey((k) => k + 1); };

  const isMB = selected?.coloration === 'methylene_blue';
  const revealed = solved || gaveUp;

  const canCheck = selected
    ? isMB
      ? Boolean(guess.kind)
      : concrete(guess.gram) && concrete(guess.shape)
    : false;

  const setGuessPart = (part: Partial<Guess>) => {
    if (revealed) return;
    setGuess((g) => ({ ...g, ...part }));
    if (checked) setChecked(false);
  };

  const check = async () => {
    if (!selected || !canCheck) return;
    const correct = isMB
      ? guess.kind === selected.results.kind
      : guess.gram === selected.results.gram && guess.shape === selected.results.shape;
    setChecked(true);
    if (correct) setSolved(true);
  };

  const kindLabel = (k?: OrganismKind) => (k ? i18n?.t(`${b}.kind-${k}`) : undefined);

  const revealResults = selected
    ? {
        gram: selected.results.gram,
        gram_proba: selected.results.gram_proba ?? 1,
        shape: selected.results.shape,
        shape_proba: selected.results.shape_proba ?? 1,
      }
    : {};

  return (
    <Box style={styles.layout}>
      <Image crossOrigin="anonymous" alt="" style={styles.backgroundImage} source={BackgroundSVG} />
      <ScrollView contentContainerStyle={{ paddingBottom: 48 }}>
        {!selected ? (
          <VStack gap={10}>
            <Heading size="lg">{i18n?.t(`${b}.title`)}</Heading>
            <Text size="md" color={COLORS.NEUTRAL[90]}>{i18n?.t(`${b}.subtitle`)}</Text>

            {samples.length === 0 ? (
              <Text style={{ marginTop: 16 }}>{i18n?.t(`${b}.empty`)}</Text>
            ) : (
              <HStack flexWrap="wrap" gap={16} style={{ marginTop: 12 }}>
                {samples.map((s) => (
                  <Pressable key={s.id} onPress={() => open(s)} style={styles.card}>
                    <Image source={{ uri: s.imageUrl }} alt="" style={styles.thumb} resizeMode="cover" />
                    <HStack justifyContent="space-between" alignItems="center" style={{ padding: 10 }}>
                      <Text fontWeight="bold">{i18n?.t(`${b}.sample`)}</Text>
                      <Box style={styles.stainTag}>
                        <Text fontSize={11} color={COLORS.NEUTRAL[90]}>
                          {i18n?.t(`${b}.stain-${s.coloration === 'methylene_blue' ? 'mb' : 'gram'}`)}
                        </Text>
                      </Box>
                    </HStack>
                  </Pressable>
                ))}
              </HStack>
            )}
          </VStack>
        ) : (
          <VStack gap={16} style={{ width: '100%', maxWidth: 1080 }}>
            <Pressable onPress={backToGallery}>
              <HStack gap={6} alignItems="center">
                <ArrowLeftIcon size={16} color={COLORS.ACCENT[100]} />
                <Text fontSize={13} color={COLORS.ACCENT[100]}>{i18n?.t(`${b}.back`)}</Text>
              </HStack>
            </Pressable>

            <Box style={styles.detailCard}>
              <HStack flexWrap="wrap" alignItems="stretch">
                <Box style={styles.detailImageWrap}>
                  <Image source={{ uri: selected.imageUrl }} alt="" style={styles.detailImage} resizeMode="cover" />
                </Box>
                <VStack gap={16} style={styles.detailPanel}>
                {/* Guess UI — hidden once revealed (the result already shows your guess) */}
                {!revealed && (isMB ? (
                  <VStack gap={8}>
                    <Text fontWeight="bold" fontSize={14}>{i18n?.t(`${b}.kind-label`)}</Text>
                    <HStack gap={8} flexWrap="wrap">
                      {KINDS.map((k) => {
                        const active = guess.kind === k;
                        return (
                          <Button
                            key={k}
                            onPress={() => setGuessPart({ kind: k })}
                            isDisabled={revealed}
                            bg={active ? COLORS.ACCENT[100] : 'transparent'}
                            borderWidth={1}
                            borderColor={COLORS.ACCENT[100]}
                            rounded="$lg"
                            flex={1}
                            style={{ minWidth: 110 }}
                            $hover-bg={active ? COLORS.ACCENT[110] : COLORS.NEUTRAL[60]}
                          >
                            <ButtonText style={{ color: active ? COLORS.NEUTRAL[0] : COLORS.ACCENT[100] }}>{kindLabel(k)}</ButtonText>
                          </Button>
                        );
                      })}
                    </HStack>
                  </VStack>
                ) : (
                  <HypothesisSection
                    value={{ gram: guess.gram, shape: guess.shape }}
                    onChange={(next) => setGuessPart({ gram: next.gram, shape: next.shape })}
                    showGram
                    isDisabled={revealed}
                  />
                ))}

                {!revealed ? (
                  <VStack gap={10}>
                    {checked && !solved && (
                      <Box style={{ backgroundColor: '#FAECE7', borderRadius: 10, padding: 12 }}>
                        <Text fontWeight="bold" color="#993C1D">{i18n?.t(`${b}.retry`)}</Text>
                      </Box>
                    )}
                    <PrimaryButton style={{ alignSelf: 'center' }} isDisable={!canCheck} content={i18n?.t(`${b}.check`)} onPress={check} />
                    {checked && !solved && (
                      <Pressable onPress={() => setGaveUp(true)} style={{ alignSelf: 'center' }}>
                        <Text fontSize={13} color={COLORS.NEUTRAL[90]} style={{ textDecorationLine: 'underline' }}>{i18n?.t(`${b}.give-up`)}</Text>
                      </Pressable>
                    )}
                  </VStack>
                ) : (
                  <VStack gap={14}>
                    <Box style={{ backgroundColor: solved ? '#E1F5EE' : '#EEF1F4', borderRadius: 10, padding: 12 }}>
                      <Text fontWeight="bold" color={solved ? '#0F6E56' : COLORS.TERCIARY[100]}>
                        {solved ? i18n?.t(`${b}.correct`) : i18n?.t(`${b}.answer`)}
                      </Text>
                    </Box>

                    {isMB ? (
                      <Box style={{ borderRadius: 12, backgroundColor: COLORS.NEUTRAL[0], borderWidth: 1, borderColor: COLORS.NEUTRAL[70], padding: 16 }}>
                        <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={8}>
                          <HStack alignItems="center" gap={10} style={{ flex: 1 }}>
                            <Text fontSize={13} fontWeight="bold" color={COLORS.NEUTRAL[90]} style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                              {i18n?.t(`${b}.type`)}
                            </Text>
                            <Box style={{ backgroundColor: '#EEF1F4', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 3 }}>
                              <Text fontWeight="bold" fontSize={14} color={COLORS.TERCIARY[100]}>{kindLabel(selected.results.kind)}</Text>
                            </Box>
                          </HStack>
                          <Box style={{ backgroundColor: solved ? '#E1F5EE' : '#FAECE7', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 5 }}>
                            <Text fontSize={12} fontWeight="bold" color={solved ? '#0F6E56' : '#993C1D'}>
                              {solved ? i18n?.t(`${b}.correct-pill`) : i18n?.t(`${b}.wrong-pill`)}
                            </Text>
                          </Box>
                        </HStack>
                        <Text fontSize={12} color={COLORS.NEUTRAL[90]} style={{ marginTop: 8 }}>
                          {i18n?.t(`${b}.your-guess`)}: {kindLabel(guess.kind) ?? '—'}
                        </Text>
                      </Box>
                    ) : (
                      <HypothesisComparison
                        hypothesis={{ gram: guess.gram, shape: guess.shape }}
                        results={revealResults as Record<string, number | string>}
                        coloration={selected.coloration}
                      />
                    )}

                    {selected.organism ? (
                      <Box style={{ backgroundColor: '#EEF1F4', borderRadius: 10, padding: 12 }}>
                        <Text fontSize={13} color={COLORS.NEUTRAL[90]}>{i18n?.t(`${b}.organism`)}</Text>
                        <Text fontWeight="bold" fontSize={16} color={COLORS.TERCIARY[100]}>{selected.organism}</Text>
                      </Box>
                    ) : null}

                    <SecundaryButton style={{ alignSelf: 'center' }} content={i18n?.t(`${b}.another`)} onPress={backToGallery} />
                  </VStack>
                )}
                </VStack>
              </HStack>
            </Box>
          </VStack>
        )}
      </ScrollView>
    </Box>
  );
};

const styles = StyleSheet.create({
  layout: { backgroundColor: COLORS.FOUNDATION_PRIMARY, height: '100%', paddingHorizontal: '10%', paddingTop: 24 },
  backgroundImage: { position: 'absolute', width: 450, height: 450, top: 0, right: -60, zIndex: -1 },
  card: { width: 200, backgroundColor: COLORS.NEUTRAL[0], borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: COLORS.NEUTRAL[70] },
  thumb: { width: 200, height: 150, backgroundColor: COLORS.NEUTRAL[60] },
  stainTag: { backgroundColor: COLORS.NEUTRAL[60], borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2 },
  detailCard: { backgroundColor: COLORS.NEUTRAL[0], borderRadius: 12, overflow: 'hidden', borderWidth: 1, borderColor: COLORS.NEUTRAL[70] },
  // Two columns on wide screens: image left, guess/result right (fills the empty
  // space, no scroll); wraps to stacked on narrow screens.
  detailImageWrap: { flexGrow: 1, flexBasis: 440, minWidth: 300 },
  // Fills the column height so it matches the panel (no whitespace below); minHeight
  // keeps it visible when the columns stack on narrow screens.
  detailImage: { width: '100%', height: '100%', minHeight: 400, backgroundColor: COLORS.NEUTRAL[60] },
  detailPanel: { flexGrow: 1, flexBasis: 380, minWidth: 320, padding: 20 },
});
